/**
 * WorldEventSystem — Dynamic world events that fire periodically during exploration.
 * Events create narrative variety and meaningful choices while traveling.
 *
 * Events are categorized as: travel, discovery, encounter, crisis
 * Up to 3 events can be active at once. Each has a cooldown before it can fire again.
 *
 * Integration:
 *   - Call WorldEventSystem.init() in main.js after other systems
 *   - Call WorldEventSystem.tick(deltaMs) from game loop or TimeSystem
 *   - WorldEventPanel shows active events as modal choice dialogs
 *
 * @module WorldEventSystem
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Maximum number of concurrently active events */
const MAX_ACTIVE_EVENTS = 3;

/** Minimum real-time ms between event tick checks */
const TICK_INTERVAL_MS = 30_000; // 30 seconds

/** Base probability of an event firing per tick (adjusted by event conditions) */
const BASE_FIRE_CHANCE = 0.15;

/** Maximum entries in resolved event history */
const MAX_HISTORY = 20;

// ─── WorldEventSystem ─────────────────────────────────────────────────────────

const WorldEventSystem = {
  /** @type {object[]} Currently active (unresolved) events */
  activeEvents: [],

  /** @type {object[]} Last MAX_HISTORY resolved events */
  eventHistory: [],

  /** @type {object[]|null} Loaded event definitions from world_events.json */
  _definitions: null,

  /** @type {Map<string, number>} eventId -> timestamp when cooldown expires */
  _cooldowns: new Map(),

  /** @type {number} Accumulated ms since last tick check */
  _accumulated: 0,

  /** @type {boolean} */
  _initialized: false,

  // ── Init ───────────────────────────────────────────────────────────────────

  /**
   * Initialize the system — load event definitions.
   * @returns {Promise<void>}
   */
  async init() {
    try {
      const mod = await import('../data/world_events.json', { assert: { type: 'json' } });
      this._definitions = mod.default ?? mod;
    } catch (importErr) {
      DebugLog.warn(`[world] WorldEventSystem: could not load world_events.json — ${importErr.message}`);
      this._definitions = [];
    }

    this._initialized = true;
    DebugLog.info(`[world] WorldEventSystem initialized — ${this._definitions?.length ?? 0} events loaded`);
  },

  // ── Tick ───────────────────────────────────────────────────────────────────

  /**
   * Called from the game loop. Accumulates time and checks whether a new
   * event should fire when the party is actively exploring.
   * @param {number} deltaMs
   */
  tick(deltaMs) {
    if (!this._initialized || !this._definitions?.length) return;
    if (this.activeEvents.length >= MAX_ACTIVE_EVENTS) return;

    this._accumulated += deltaMs;
    if (this._accumulated < TICK_INTERVAL_MS) return;
    this._accumulated = 0;

    // Attempt to fire an event
    this._tryFireRandomEvent();
  },

  /**
   * Select and fire a random eligible event based on current world state.
   * @private
   */
  _tryFireRandomEvent() {
    if (!this._definitions?.length) return;

    const now = Date.now();
    const worldState = window.__worldState;
    const currentBiome = worldState?.currentBiome ?? 'plains';
    const currentRegionLevel = worldState?.currentRegionLevel ?? 1;
    const currentFame = window.__worldState?.partyFame ?? 0;

    // Filter to eligible events
    const eligible = this._definitions.filter(def => {
      // Skip already active
      if (this.activeEvents.some(e => e.id === def.id)) return false;

      // Check cooldown
      const cooldownEnd = this._cooldowns.get(def.id) ?? 0;
      if (now < cooldownEnd) return false;

      // Check biome condition
      const tc = def.trigger_conditions ?? {};
      if (tc.biomes?.length && !tc.biomes.includes(currentBiome)) return false;

      // Check region level
      if (tc.min_region_level && currentRegionLevel < tc.min_region_level) return false;

      // Check fame
      if (tc.min_fame !== undefined && currentFame < tc.min_fame) return false;

      return true;
    });

    if (!eligible.length) return;

    // Roll for whether an event fires at all
    if (Math.random() > BASE_FIRE_CHANCE) return;

    // Pick a random eligible event
    const def = eligible[Math.floor(Math.random() * eligible.length)];
    this._fireEvent(def);
  },

  /**
   * Instantiate and activate an event.
   * @param {object} def — Event definition from world_events.json
   * @private
   */
  _fireEvent(def) {
    const event = {
      id: def.id,
      name: def.name,
      category: def.category,
      description: def.description,
      choices: def.choices,
      firedAt: Date.now(),
    };

    this.activeEvents.push(event);

    DebugLog.event(`[world] WorldEvent fired; id=${event.id} name="${event.name}" category=${event.category}`);
    EventBus.emit('worldEvent:fired', { event });
  },

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Force-fire a specific event by id. Used for debug/testing.
   * @param {string} eventId
   * @returns {string} Result message
   */
  triggerEvent(eventId) {
    if (!this._definitions) return 'WorldEventSystem not initialized';

    const def = this._definitions.find(d => d.id === eventId);
    if (!def) return `Unknown event id: "${eventId}"`;

    if (this.activeEvents.some(e => e.id === eventId)) {
      return `Event "${eventId}" is already active`;
    }

    if (this.activeEvents.length >= MAX_ACTIVE_EVENTS) {
      return `Cannot add event — already at maximum active events (${MAX_ACTIVE_EVENTS})`;
    }

    this._fireEvent(def);
    return `Event triggered: ${def.name}`;
  },

  /**
   * Player resolves an active event by making a choice.
   * Applies the outcome and removes the event from active list.
   * @param {string} eventId
   * @param {string} choiceId
   * @returns {{ ok: boolean, message: string, outcome: object|null }}
   */
  resolveEvent(eventId, choiceId) {
    const eventIndex = this.activeEvents.findIndex(e => e.id === eventId);
    if (eventIndex === -1) {
      return { ok: false, message: `No active event with id "${eventId}"`, outcome: null };
    }

    const event = this.activeEvents[eventIndex];
    const choice = event.choices?.find(c => c.id === choiceId);

    if (!choice) {
      return { ok: false, message: `Unknown choice "${choiceId}" for event "${eventId}"`, outcome: null };
    }

    // Remove from active events
    this.activeEvents.splice(eventIndex, 1);

    // Set cooldown
    const def = this._definitions?.find(d => d.id === eventId);
    if (def?.cooldown_hours) {
      this._cooldowns.set(eventId, Date.now() + def.cooldown_hours * 3_600_000);
    }

    // Record in history
    const resolved = {
      ...event,
      resolvedAt: Date.now(),
      choiceId,
      choiceText: choice.text,
      outcome: choice.outcome,
    };
    this.eventHistory.unshift(resolved);
    if (this.eventHistory.length > MAX_HISTORY) {
      this.eventHistory.pop();
    }

    // Apply outcome
    const result = this._applyOutcome(choice.outcome, event);

    DebugLog.event(
      `[world] WorldEvent resolved; id=${eventId} choice=${choiceId} outcome=${choice.outcome?.type}`
    );
    EventBus.emit('worldEvent:resolved', { event: resolved, outcome: choice.outcome });

    return { ok: true, message: result.message, outcome: choice.outcome };
  },

  /**
   * Apply the mechanical effect of an event outcome.
   * @param {object} outcome
   * @param {object} event
   * @returns {{ message: string }}
   * @private
   */
  _applyOutcome(outcome, _event) {
    if (!outcome) return { message: 'Nothing happens.' };

    const WorldState = window.__worldState;

    switch (outcome.type) {
      case 'nothing':
        return { message: 'You continue on your way.' };

      case 'fame_delta': {
        const delta = outcome.fame_delta ?? 0;
        if (delta && WorldState) {
          WorldState.partyFame = Math.max(-1000, Math.min(1000,
            (WorldState.partyFame ?? 0) + delta
          ));
        }
        return { message: delta > 0 ? `Your fame increases by ${delta}.` : `Your fame decreases by ${Math.abs(delta)}.` };
      }

      case 'gold_cost': {
        const gold = outcome.gold ?? 0;
        if (WorldState) {
          WorldState.partyGold = Math.max(0, (WorldState.partyGold ?? 0) - gold);
        }
        const fameDelta = outcome.fame_delta ?? 0;
        if (fameDelta && WorldState) {
          WorldState.partyFame = Math.max(-1000, Math.min(1000,
            (WorldState.partyFame ?? 0) + fameDelta
          ));
        }
        return { message: `You pay ${gold} gold.` };
      }

      case 'resource_cost': {
        const rations = outcome.ration_cost ?? 0;
        if (rations && WorldState) {
          WorldState.rationPoints = Math.max(0, (WorldState.rationPoints ?? 0) - rations);
        }
        const fameDelta = outcome.fame_delta ?? 0;
        if (fameDelta && WorldState) {
          WorldState.partyFame = Math.max(-1000, Math.min(1000,
            (WorldState.partyFame ?? 0) + fameDelta
          ));
        }
        return { message: `You spend ${rations} ration points.` };
      }

      case 'shop_discount':
        return { message: `Discounted goods available! ${Math.round((outcome.discount ?? 0) * 100)}% off.` };

      case 'buff':
        return { message: `Your party gains: ${outcome.effect ?? 'unknown buff'}.` };

      case 'blessing':
        return { message: `The grove blesses your party with: ${outcome.effect ?? 'unknown blessing'}.` };

      case 'loot_and_curse': {
        const goldMin = outcome.gold?.[0] ?? 0;
        const goldMax = outcome.gold?.[1] ?? 0;
        const gold = Math.floor(Math.random() * (goldMax - goldMin + 1)) + goldMin;
        if (WorldState) {
          WorldState.partyGold = (WorldState.partyGold ?? 0) + gold;
        }
        const fameDelta = outcome.fame_delta ?? 0;
        if (fameDelta && WorldState) {
          WorldState.partyFame = Math.max(-1000, Math.min(1000,
            (WorldState.partyFame ?? 0) + fameDelta
          ));
        }
        return { message: `You find ${gold} gold but are cursed with: ${outcome.curse_effect ?? 'unknown curse'}.` };
      }

      case 'loot': {
        const goldRange = outcome.gold;
        if (goldRange && WorldState) {
          const gold = Math.floor(Math.random() * (goldRange[1] - goldRange[0] + 1)) + goldRange[0];
          WorldState.partyGold = (WorldState.partyGold ?? 0) + gold;
          return { message: `You find ${gold} gold.` };
        }
        return { message: 'You find some useful items.' };
      }

      case 'loot_and_lore': {
        const goldRange = outcome.gold;
        if (goldRange && WorldState) {
          const gold = Math.floor(Math.random() * (goldRange[1] - goldRange[0] + 1)) + goldRange[0];
          WorldState.partyGold = (WorldState.partyGold ?? 0) + gold;
          return { message: `You find ${gold} gold and learn ancient lore.` };
        }
        return { message: 'You discover ancient lore.' };
      }

      case 'loot_or_trap': {
        const isLoot = Math.random() < (outcome.loot_chance ?? 0.5);
        if (isLoot) {
          const goldRange = outcome.loot_gold ?? [0, 0];
          const gold = Math.floor(Math.random() * (goldRange[1] - goldRange[0] + 1)) + goldRange[0];
          if (WorldState) WorldState.partyGold = (WorldState.partyGold ?? 0) + gold;
          return { message: `Lucky! You find ${gold} gold in the camp.` };
        } else {
          const dmgRange = outcome.trap_damage ?? [5, 15];
          const dmg = Math.floor(Math.random() * (dmgRange[1] - dmgRange[0] + 1)) + dmgRange[0];
          return { message: `It was a trap! The party takes ${dmg} damage.` };
        }
      }

      case 'lose_time':
        return { message: `You lose ${outcome.hours ?? 1} hours.` };

      case 'risk_injury': {
        const injured = Math.random() < (outcome.injury_chance ?? 0.3);
        if (injured) {
          return { message: `Pushing through the storm, some party members are injured.` };
        }
        return { message: 'Your party pushes through without major harm.' };
      }

      case 'alignment_shift': {
        const shift = outcome.shift ?? 'neutral';
        if (WorldState) WorldState.partyAlignment = shift;
        const fameDelta = outcome.fame_delta ?? 0;
        if (fameDelta && WorldState) {
          WorldState.partyFame = Math.max(-1000, Math.min(1000,
            (WorldState.partyFame ?? 0) + fameDelta
          ));
        }
        return { message: `Your alignment shifts toward ${shift}.` };
      }

      case 'map_marker':
        return { message: 'Location marked on your map.' };

      case 'reveal_locations':
        return { message: `${outcome.count ?? 1} locations revealed on your map.` };

      case 'add_item':
        return { message: `Added ${outcome.qty ?? 1}x ${outcome.item_id ?? 'item'} to inventory.` };

      case 'item_find':
        return { message: 'You find useful equipment on the battlefield.' };

      case 'random_buff_debuff': {
        const isBuff = Math.random() < (outcome.buff_chance ?? 0.5);
        return { message: isBuff ? 'The magical water grants a blessing!' : 'The magical water causes a strange effect...' };
      }

      case 'trigger_combat': {
        const fameDelta = outcome.fame_delta ?? 0;
        if (fameDelta && WorldState) {
          WorldState.partyFame = Math.max(-1000, Math.min(1000,
            (WorldState.partyFame ?? 0) + fameDelta
          ));
        }
        EventBus.emit('encounter:trigger', {
          enemyPool: outcome.enemy_pool ?? [],
          countRange: outcome.count ?? [1, 2],
        });
        return { message: 'Combat begins!' };
      }

      case 'skill_check': {
        // Resolve skill check outcome — use best party attribute vs DC
        const dc = outcome.dc ?? 10;
        const attr = outcome.attribute ?? 'might';
        const CharSystem = window.__characterSystem;
        let bestScore = 8; // fallback

        if (CharSystem?.getAll) {
          const heroes = CharSystem.getAll().filter(c => c.isHero);
          for (const hero of heroes) {
            const score = hero.attributes?.[attr] ?? 8;
            if (score > bestScore) bestScore = score;
          }
        }

        const modifier = Math.floor((bestScore - 10) / 2);
        const roll = Math.floor(Math.random() * 20) + 1;
        const total = roll + modifier;
        const success = total >= dc;

        DebugLog.event(`[world] Skill check; attr=${attr} dc=${dc} roll=${roll} mod=${modifier} total=${total} success=${success}`);

        const resultOutcome = success ? (outcome.success ?? { type: 'nothing' }) : (outcome.failure ?? { type: 'nothing' });
        return this._applyOutcome(resultOutcome, _event);
      }

      case 'world_flag':
        if (window.__worldState) {
          window.__worldState._flags = window.__worldState._flags ?? {};
          window.__worldState._flags[outcome.flag] = true;
        }
        return { message: `World state updated: ${outcome.flag}` };

      case 'start_quest': {
        const fameDelta = outcome.fame_delta ?? 0;
        if (fameDelta && WorldState) {
          WorldState.partyFame = Math.max(-1000, Math.min(1000,
            (WorldState.partyFame ?? 0) + fameDelta
          ));
        }
        return { message: `A new quest begins: ${outcome.quest_id ?? 'unknown'}.` };
      }

      default:
        DebugLog.warn(`[world] WorldEventSystem: unknown outcome type "${outcome.type}"`);
        return { message: 'Something happens...' };
    }
  },

  // ── Queries ────────────────────────────────────────────────────────────────

  /**
   * @returns {object[]} Currently active events
   */
  getActiveEvents() {
    return [...this.activeEvents];
  },

  /**
   * @returns {object[]} Recently resolved event history
   */
  getHistory() {
    return [...this.eventHistory];
  },

  /**
   * @returns {object[]} All event definitions
   */
  getDefinitions() {
    return this._definitions ?? [];
  },

  // ── Serialization ──────────────────────────────────────────────────────────

  /**
   * Return serializable state for SaveSystem.
   * @returns {object}
   */
  getState() {
    return {
      activeEvents: [...this.activeEvents],
      eventHistory: [...this.eventHistory],
      cooldowns: Array.from(this._cooldowns.entries()),
    };
  },

  /**
   * Restore system state from a save snapshot.
   * @param {object} data
   */
  restoreState(data) {
    if (!data) return;
    this.activeEvents = data.activeEvents ?? [];
    this.eventHistory = data.eventHistory ?? [];
    this._cooldowns = new Map(data.cooldowns ?? []);
    DebugLog.info('[world] WorldEventSystem state restored');
  },
};

export default WorldEventSystem;
